import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  getAuth,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, app } from '../config/firebase';
import { initializeApp, deleteApp } from 'firebase/app';
import { AdminUser } from '../types';

export const authService = {
  login: async (email: string, password: string): Promise<AdminUser> => {
    await setPersistence(auth, browserSessionPersistence);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check if user is an admin
    const adminDoc = await getDoc(doc(db, 'adminUsers', user.uid));
    if (!adminDoc.exists()) {
      // Check in users collection with admin role
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists() || userDoc.data().role !== 'admin') {
        throw new Error('Access denied. Admin access required.');
      }
      const userData = userDoc.data();
      const adminUser: AdminUser = {
        id: user.uid,
        email: user.email || '',
        name: userData.name || 'Admin',
        role: userData.adminData?.role || 'admin',
      };
      localStorage.setItem('admin_user', JSON.stringify(adminUser)); // Keep for now if needed, but the requirement is "every open"
      sessionStorage.setItem('admin_user', JSON.stringify(adminUser));
      return adminUser;
    }

    const adminData = adminDoc.data();
    const adminUser: AdminUser = {
      id: user.uid,
      email: user.email || '',
      name: adminData.name || 'Admin',
      role: adminData.role || 'admin',
    };
    localStorage.setItem('admin_user', JSON.stringify(adminUser)); // Keep for consistency if needed, but priority is session
    sessionStorage.setItem('admin_user', JSON.stringify(adminUser));
    return adminUser;
  },

  logout: async () => {
    localStorage.removeItem('admin_user');
    sessionStorage.removeItem('admin_user');
    await signOut(auth);
  },

  getCurrentUser: (): AdminUser | null => {
    const userStr = sessionStorage.getItem('admin_user') || localStorage.getItem('admin_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  onAuthStateChange: (callback: (user: FirebaseUser | null) => void) => {
    return onAuthStateChanged(auth, callback);
  },

  isAuthenticated: (): boolean => {
    return auth.currentUser !== null && (sessionStorage.getItem('admin_user') !== null || localStorage.getItem('admin_user') !== null);
  },

  createAdmin: async (data: Omit<AdminUser, 'id'> & { password: string }): Promise<void> => {
    // We use a secondary auth instance to create a new user without signing out the current admin
    const secondaryApp = initializeApp(app.options, 'Secondary');
    const secondaryAuth = getAuth(secondaryApp);

    try {
      const { user: newUser } = await createUserWithEmailAndPassword(
        secondaryAuth,
        data.email,
        data.password
      );

      // Create admin document in adminUsers collection
      await setDoc(doc(db, 'adminUsers', newUser.uid), {
        name: data.name,
        email: data.email,
        role: data.role,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Optionally update users collection as well if that's the system's pattern
      await setDoc(doc(db, 'users', newUser.uid), {
        uid: newUser.uid,
        email: data.email,
        role: 'admin',
        adminData: {
          name: data.name,
          role: data.role,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } finally {
      // Sign out and delete the secondary app to clean up
      await secondaryAuth.signOut();
      await deleteApp(secondaryApp);
    }
  },
};
